import React from "react";
import { IconButton } from "@mui/material";
import { useSlidingAppearance } from "../lib/AppearanceContext";

/** iOS minimum touch target (pt). */
const MIN_TOUCH_PT = 44;

/** Default chevron size at Dynamic Type “Large” (17pt). */
const DEFAULT_FONT_SIZE = "1.0625rem";

/**
 * SF Symbol–style chevron paths (chevron.right / chevron.left geometry).
 * Stroke weight matches ~semibold SF Symbol at 17pt.
 */
function SFChevronIcon({ direction = "right", ...props }) {
    const mirrored = direction === "left";
    return (
        <svg
            width="1em"
            height="1.625em"
            viewBox="0 0 8 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
            focusable="false"
            style={mirrored ? { transform: "scaleX(-1)" } : undefined}
            {...props}
        >
            <path
                d="M1.25 1.25L6.25 7L1.25 12.75"
                stroke="currentColor"
                strokeWidth="2.15"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * iOS-style chromeless directional chevron button.
 *
 * - SF Symbol `chevron.right` / `chevron.left` appearance
 * - ≥ 44×44pt touch target (HIG)
 * - Scales with Dynamic Type via `em` + `clamp()` (override with `fontSize` in `sx`)
 * - Subtle active press state (opacity + scale, respects reduced motion)
 */
export default function IOSChevronButton({
    direction = "right",
    onClick,
    disabled = false,
    ariaLabel,
    component,
    to,
    color,
    sx,
}) {
    const { tokens, isDark } = useSlidingAppearance();
    const label =
        ariaLabel || (direction === "left" ? "Previous" : "Next");
    const iconColor = color ?? tokens.text;

    return (
        <IconButton
            component={component}
            to={to}
            type="button"
            onClick={onClick}
            disableRipple
            disableFocusRipple
            disabled={disabled}
            aria-label={label}
            sx={{
                // Dynamic Type: rem/em track user text-size preferences in WebView.
                fontSize: DEFAULT_FONT_SIZE,
                width: `max(${MIN_TOUCH_PT}px, 2.75em)`,
                height: `max(${MIN_TOUCH_PT}px, 2.75em)`,
                minWidth: MIN_TOUCH_PT,
                minHeight: MIN_TOUCH_PT,
                borderRadius: "50%",
                p: 0,
                color: iconColor,
                bgcolor: "transparent",
                boxShadow: "none",
                border: "none",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
                flexShrink: 0,
                transition:
                    "transform 0.14s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.14s ease, background-color 0.14s ease",
                "&:hover:not(.Mui-disabled)": {
                    "@media (hover: hover)": {
                        bgcolor: isDark ? "rgba(59, 166, 102, 0.12)" : "rgba(15, 23, 42, 0.04)",
                    },
                },
                "&:active:not(.Mui-disabled)": {
                    transform: "scale(0.96)",
                    opacity: 0.55,
                    bgcolor: isDark ? "rgba(59, 166, 102, 0.2)" : "rgba(15, 23, 42, 0.07)",
                },
                "&.Mui-focusVisible": {
                    outline: "2px solid rgba(59, 166, 102, 0.55)",
                    outlineOffset: 2,
                },
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "opacity 0.14s ease, background-color 0.14s ease",
                    "&:active:not(.Mui-disabled)": {
                        transform: "none",
                    },
                },
                "&.Mui-disabled": {
                    opacity: 0.28,
                    color: iconColor,
                },
                ...sx,
            }}
        >
            <SFChevronIcon direction={direction} />
        </IconButton>
    );
}

export { SFChevronIcon, MIN_TOUCH_PT, DEFAULT_FONT_SIZE };
